'use client'

import { useMemo, useState } from 'react'
import { TagInstallPanel } from '@/components/admin/TagInstallPanel'
import { ContainerManager } from '@/components/tags/ContainerManager'
import { InstallCodeBlock } from '@/components/developer/InstallCodeBlock'
import { cn } from '@/lib/utils'
import {
  DeveloperSubNav,
  developerTabLabel,
  type DeveloperTab,
} from '@/components/developer/DeveloperSubNav'
import {
  DEVELOPER_EXAMPLE_INSTALL_SNIPPET,
  DEVELOPER_EXAMPLE_INSTALL_STATUS_URL,
  getDeveloperExampleContainerPayload,
} from '@/lib/developer/fallback-data'

type Props = {
  apiBase: string
  containerId: string
  displayName?: string
  initialInstallSnippet?: string
  exampleData?: boolean
}

function ComingSoonPanel({ tab }: { tab: DeveloperTab }) {
  return (
    <div className="mt-8 rounded-card border-[0.5px] border-line bg-surface px-6 py-16 text-center">
      <p className="text-sm font-medium text-text-strong">{developerTabLabel(tab)}</p>
      <p className="mt-1 text-[13px] text-text-muted">Coming soon.</p>
    </div>
  )
}

export function DeveloperPageClient({
  apiBase,
  containerId,
  displayName,
  initialInstallSnippet = '',
  exampleData = false,
}: Props) {
  const [tab, setTab] = useState<DeveloperTab>('overview')
  const installSnippet = exampleData ? DEVELOPER_EXAMPLE_INSTALL_SNIPPET : initialInstallSnippet
  const statusUrl = exampleData ? DEVELOPER_EXAMPLE_INSTALL_STATUS_URL : `${apiBase}/install-status`
  const examplePayload = useMemo(
    () => (exampleData ? getDeveloperExampleContainerPayload() : undefined),
    [exampleData],
  )

  return (
    <div className="min-h-full bg-canvas text-text-strong">
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-8">
        <div className="pb-16 pt-6">
          <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em] text-text-strong sm:text-[34px]">
            Developers
          </h1>

          <div className="mt-6 flex justify-center sm:justify-start">
            <DeveloperSubNav active={tab} onChange={setTab} />
          </div>

          {tab === 'overview' ? (
            <>
              <div className="mt-8 overflow-hidden rounded-card border-[0.5px] border-line bg-surface">
                <div className="grid divide-y divide-line-subtle md:grid-cols-2 md:divide-x md:divide-y-0">
                  <div className="p-6 sm:p-8">
                    <h2 className="text-[18px] font-semibold text-text-strong">Developer quickstart</h2>
                    <p className="mt-2 text-[13.5px] leading-[1.55] text-text-muted">
                      Paste the install snippet in the {'<head>'} of every page on your site. One
                      install covers all tags and triggers configured in your container.
                    </p>
                    <div className="mt-5">
                      <TagInstallPanel
                        statusUrl={statusUrl}
                        installSnippet={installSnippet}
                        section="install"
                        hideSnippet
                      />
                    </div>
                  </div>
                  <div className="relative min-h-[280px] md:min-h-0">
                    <InstallCodeBlock snippet={installSnippet} className="absolute inset-0" />
                  </div>
                </div>
              </div>

              <section className="mt-8">
                <div className="mb-4">
                  <h2 className="text-[18px] font-semibold text-text-strong">Tag containers</h2>
                  <p className="mt-1 text-[13.5px] text-text-muted">
                    Configure tags, triggers, variables, and publish changes to your live container.
                  </p>
                </div>
                <div
                  className={cn(
                    'overflow-hidden rounded-card border-[0.5px] border-line bg-surface',
                    'h-[min(72vh,760px)]',
                  )}
                >
                  <ContainerManager
                    containerId={containerId}
                    apiBase={apiBase}
                    displayName={displayName}
                    hideHeader
                    exampleMode={exampleData}
                    examplePayload={examplePayload}
                    exampleInstallStatusUrl={DEVELOPER_EXAMPLE_INSTALL_STATUS_URL}
                  />
                </div>
              </section>
            </>
          ) : (
            <ComingSoonPanel tab={tab} />
          )}
        </div>
      </div>
    </div>
  )
}
